import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import StyledModal from "../CustomModal";
import { FooterButtons, FormWrapper, StyledButton } from "../styles";
import { Select, Form, DatePicker } from "antd";
import { dateRangeFilterOptions, foundDateRangeFilterOptions } from "../../utils/constants/filterOption";
import { useAppSelector } from "../../utils/hooks/redux";
import { ReportType } from "../../redux/features/board/columnTypes";
import { handleMergeDateTime } from "../../utils/helpers/dateFunctions";

dayjs.extend(isBetween);
interface DatePickerModalProps {
  isModalOpen: boolean;
  handleOk: (params: { dateField?: string; fromDate?: string; toDate?: string }) => void;
  handleCancel: () => void;
  values: { dateField?: string; fromDate?: string; toDate?: string }; // received from Redux or parent component
}

const DatePickerModal: React.FC<DatePickerModalProps> = ({ isModalOpen, handleOk, handleCancel, values }) => {
  const [selectedRange, setSelectedRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(values.fromDate && values.toDate ? [dayjs(values.fromDate), dayjs(values.toDate)] : [dayjs().startOf("month"), dayjs().endOf("month")]);
  const [form] = Form.useForm();
  Form.useWatch([], form);
  const initReportType = useAppSelector((state) => state.columns.initReportType);

  const dateFilterOptions = useMemo(() => {
    if (initReportType === ReportType.Found) {
      return [...foundDateRangeFilterOptions]
    }

    return [...dateRangeFilterOptions]
  }, [initReportType, dateRangeFilterOptions, foundDateRangeFilterOptions])

  useEffect(() => {
    // Set default form values whenever the modal opens or values change
    form.setFieldsValue({
      dateField: values.dateField || dateFilterOptions[0]?.value,
      dateRange: values.fromDate && values.toDate ? [dayjs(values.fromDate), dayjs(values.toDate)] : undefined,
    });
  }, [values, form]);

  const disabledDate = (current: any) => {
    const currentDayjs = dayjs(current);
    const today = dayjs();
    const oneYearAgo = today.subtract(1, 'year').startOf('day');
    const endOfCurrentMonth = today.endOf('month');

    return currentDayjs.isBefore(oneYearAgo, 'day') || currentDayjs.isAfter(endOfCurrentMonth, 'day');
  };

  const handleSubmit = async () => {
    try {
      // Validate form first
      const values = await form.validateFields();
      const { dateField, dateRange } = values;

      // If either field is not selected, we send undefined
      if (!dateField || !dateRange || !dateRange[0] || !dateRange[1]) {
        handleOk({ dateField: undefined, fromDate: undefined, toDate: undefined });
      } else {
        handleOk({
          dateField,
          fromDate: dayjs(dateRange[0]).isValid() ? handleMergeDateTime(dateRange[0])?.toISOString() : undefined,
          toDate: dayjs(dateRange[1]).isValid() ? handleMergeDateTime(dateRange[1])?.toISOString() : undefined,
        });
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <StyledModal
      draggable={true}
      title={"Date Range"}
      open={isModalOpen}
      closeIcon={null}
      footer={
        <FooterButtons>
          <StyledButton onClick={handleCancel} disabled={false}>
            Cancel
          </StyledButton>
          <StyledButton type="primary" onClick={handleSubmit} loading={false}>
            Save
          </StyledButton>
        </FooterButtons>
      }
      centered
      onCancel={handleCancel}
    >
      <FormWrapper form={form} layout="vertical" validateTrigger="onSubmit" initialValues={values}>
        <Form.Item name="dateField" label={"Date Type"} rules={[{ required: form.getFieldValue("dateRange"), message: "Please select an option!" }]}>
          <Select allowClear defaultValue={dateFilterOptions[0]?.value} options={dateFilterOptions} size="large" placeholder="Select Date Field" />
        </Form.Item>
        <Form.Item name="dateRange" label={"Date Range"} rules={[{ required: form.getFieldValue("dateField"), message: "Please select a date range!" }]}>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            style={{ width: "100%" }}
            size="large"
            value={selectedRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setSelectedRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
              }
            }}
          />
        </Form.Item>
      </FormWrapper>
    </StyledModal>
  );
};

export default DatePickerModal;
